/**
 * Asset module translations - English
 * Includes: building, floor, shop, advertising, shared space, warehouse, parking, visualization
 */
export default {
  asset: {
    // ==================== Visual / Canvas ====================
    visual: {
      editMode: 'Edit Mode',
      exitEdit: 'Exit Edit',
      drawAsset: 'Draw Asset',
      drawShop: 'Draw Shop',
      selectShape: 'Select Shape',
      shapePrefix: 'Shape:',
      grid: 'Grid',
      gridSnap: 'Snap to Grid',
      importBgSvg: 'Import Background/SVG',
      importBg: 'Import Background',
      clearFloor: 'Clear Floor',
      fullscreen: 'Fullscreen',
      exitFullscreen: 'Exit Fullscreen',
      drawHint: 'Drawing {type}, drag to draw, press ESC to cancel',
      polygonHint: 'Click to add points, double-click or Enter to close, Esc to cancel (min 3 points)',
      reOutline: 'Re-outline position',
      noPositionTip: 'This asset has no position. Right-click "Re-outline position" in edit mode to draw it',
      reOutlineSuccess: 'Position updated',
      reOutlineFailed: 'Failed to save position',
      polygonMinPoints: 'Polygon needs at least 3 points',
      drawShopHint: 'Drag to draw a shop unit, press ESC to cancel',
      operationHint: 'Scroll to zoom | Alt+Drag to pan | Middle-click drag to pan',
      typeFilter: 'Asset Type Filter',
      statusLegend: 'Status Legend',
      legend: 'Legend',
      properties: 'Properties',
      info: 'Info',
      editDetail: 'Edit Details',
      viewDetail: 'View Details',
      copy: 'Copy',
      shopProperties: 'Shop Properties',
      shopName: 'Asset Name',
      shopCode: 'Asset Code',
      newShopName: 'New Shop {n}',
      assetInfo: 'Asset Info',
      builtArea: 'Built Area',
      rentableAreaLabel: 'Rentable Area',
      clearFloorConfirmMsg: 'This will clear all assets and background of the current floor. This action cannot be undone. Continue?',
      clearFloorConfirmTitle: 'Confirm Clear',
      clearFloorConfirmBtn: 'Confirm Clear',
      clearedCount: 'Cleared {count} assets',
      clearFailed: 'Clear failed: {msg}',
      statusRented: 'Rented',
      statusAvailable: 'Vacant',
      statusReserved: 'Reserved',
      statusMaintenance: 'Maintenance',
      confirmSvgBgTitle: 'Confirm Import Background',
      confirmSvgBgWithShapes: '{count} asset shapes detected. Import this SVG as floor background?',
      confirmSvgBgNoShapes: 'No asset shapes detected. Import this SVG as floor background?',
      confirmSvgBgWithExisting: 'Existing background or assets detected. Continuing will clear them. Proceed?',
    },
    shape: {
      rect: 'Rectangle',
      circle: 'Circle',
      ellipse: 'Ellipse',
      triangle: 'Triangle',
      diamond: 'Diamond',
      polygon: 'Polygon',
    },

    // ==================== SVG Import ====================
    svgImport: {
      title: 'SVG Floor Plan Import',
      shapesFound: '{count} shapes detected',
      unmatched: 'Unmatched',
      ignored: 'Ignored',
      totalArea: 'Total Area',
      preview: 'Preview',
      previewHint: '(Numbers correspond to the list on the right. Green=Matched, Orange=Pending, Gray=Ignored)',
      shapeList: 'Shape List',
      setUnmatchedTo: 'Set unmatched to:',
      select: 'Select',
      ignore: 'Ignore',
      noId: 'No ID',
      pendingConfirm: 'Pending',
      nameLabel: 'Name: ',
      codeLabel: 'Code: ',
      areaPlaceholder: 'Area',
      selectType: 'Select Type',
      selectDept: 'Select Dept',
      batchDept: 'Batch Dept:',
      batchArea: 'Batch Area:',
      apply: 'Apply',
      setAsBackground: 'Background only',
      resetIgnored: 'Restore ignored',
      colorMapping: 'Map by color:',
      applyColorMap: 'Apply color map',
      colorMapEmpty: 'Select an asset type for at least one color first',
      colorMapApplied: '{count} shapes assigned by color',
      autoIgnoredMsg: '{count} shapes auto-ignored as background (fragments/slender strips). Real units are sorted to the top; click "Restore ignored" if misjudged',
      confirmImport: 'Confirm Import ({count} items)',
      confirmImportMsg: '{count} assets will be imported. Existing assets on this floor will be replaced. Continue?',
      confirmImportTitle: 'Confirm Import',
      confirmImportBtn: 'Confirm Import',
      sourceDataType: 'data-type attribute',
      sourceId: 'ID prefix',
      sourceClass: 'class attribute',
      sourceColor: 'Fill color',
      sourceUnknown: 'Cannot auto-detect',
    },

    // ==================== Advertising ====================
    advertising: {
      editTitle: 'Edit Advertising Space',
      addTitle: 'Add Advertising Space',
      code: 'Ad Code',
      name: 'Ad Name',
      type: 'Ad Type',
      displayType: 'Display Type',
      widthMeter: 'Width (m)',
      heightMeter: 'Height (m)',
      area: 'Floor Area',
      rentableArea: 'Leasable Area',
      monthlyFee: 'Monthly Fee',
      managementDept: 'Management Dept',
      managementDeptPlaceholder: 'Select management department',
      managementDeptRequired: 'Management dept is required',
      codePlaceholder: 'Enter ad code',
      namePlaceholder: 'Enter ad name',
      typePlaceholder: 'Select ad type',
      displayTypePlaceholder: 'Select display type',
      widthPlaceholder: 'Enter width',
      heightPlaceholder: 'Enter height',
      areaPlaceholder: 'Enter floor area',
      rentableAreaPlaceholder: 'Enter leasable area',
      codeRequired: 'Please enter ad code',
      nameRequired: 'Please enter ad name',
      typeRequired: 'Please select ad type',
      displayTypeRequired: 'Please select display type',
      areaRequired: 'Floor area is required',
      rentableAreaRequired: 'Leasable area is required',
    },

    // ==================== Shared Space ====================
    sharedSpace: {
      editTitle: 'Edit Shared Space',
      addTitle: 'Add Shared Space',
      code: 'Space Code',
      name: 'Space Name',
      type: 'Space Type',
      areaUnit: 'Floor Area',
      rentableArea: 'Leasable Area',
      rentableAreaUnit: 'Leasable Area',
      capacityUnit: 'Capacity (pax)',
      availableTime: 'Available Hours',
      managementDept: 'Management Dept',
      managementDeptPlaceholder: 'Select management department',
      managementDeptRequired: 'Management dept is required',
      usageRestrictions: 'Usage Restrictions',
      codePlaceholder: 'Enter space code',
      namePlaceholder: 'Enter space name',
      typePlaceholder: 'Select space type',
      areaPlaceholder: 'Enter floor area',
      rentableAreaPlaceholder: 'Enter leasable area',
      capacityPlaceholder: 'Enter capacity',
      availableTimePlaceholder: 'e.g. 9:00-22:00',
      usagePlaceholder: 'Enter usage restrictions',
      codeRequired: 'Please enter space code',
      nameRequired: 'Please enter space name',
      typeRequired: 'Please select space type',
      areaRequired: 'Floor area is required',
      rentableAreaRequired: 'Leasable area is required',
      statusAvailable: 'Available',
      statusReserved: 'Reserved',
      statusMaintenance: 'Maintenance',
    },

    // ==================== Warehouse ====================
    warehouse: {
      editTitle: 'Edit Warehouse',
      addTitle: 'Add Warehouse',
      code: 'Warehouse Code',
      name: 'Warehouse Name',
      storageType: 'Storage Type',
      totalArea: 'Floor Area',
      rentableArea: 'Leasable Area',
      heightMeter: 'Ceiling Height (m)',
      loadCapacity: 'Load Capacity (tons)',
      monthlyFeePerSqm: 'Monthly Rent (per sqm)',
      monthlyFeePlaceholder: 'Enter monthly rent (per sqm)',
      facilities: 'Facilities',
      ventilation: 'Ventilation',
      fireProtection: 'Fire Protection',
      managementDept: 'Management Dept',
      managementDeptPlaceholder: 'Select management department',
      managementDeptRequired: 'Management dept is required',
      codePlaceholder: 'Enter warehouse code',
      namePlaceholder: 'Enter warehouse name',
      typePlaceholder: 'Select storage type',
      totalAreaPlaceholder: 'Enter floor area',
      rentableAreaPlaceholder: 'Enter leasable area',
      heightPlaceholder: 'Enter ceiling height',
      loadCapacityPlaceholder: 'Enter load capacity',
      codeRequired: 'Please enter warehouse code',
      nameRequired: 'Please enter warehouse name',
      typeRequired: 'Please select storage type',
      totalAreaRequired: 'Floor area is required',
      rentableAreaRequired: 'Leasable area is required',
      statusAvailable: 'Available',
      statusRented: 'Rented',
      statusMaintenance: 'Maintenance',
    },

    // ==================== Parking ====================
    parking: {
      editTitle: 'Edit Parking Space',
      addTitle: 'Add Parking Space',
      code: 'Parking Code',
      name: 'Parking Name',
      type: 'Parking Type',
      area: 'Parking Zone',
      rentableArea: 'Leasable Area',
      section: 'Section No.',
      monthlyFee: 'Monthly Rent',
      managementDept: 'Management Dept',
      managementDeptPlaceholder: 'Select management department',
      managementDeptRequired: 'Management dept is required',
      codePlaceholder: 'Enter parking code',
      namePlaceholder: 'Enter parking name',
      typePlaceholder: 'Select parking type',
      areaPlaceholder: 'Enter parking zone',
      rentableAreaPlaceholder: 'Enter leasable area',
      sectionPlaceholder: 'Enter section number',
      monthlyFeePlaceholder: 'Enter monthly rent',
      codeRequired: 'Please enter parking code',
      typeRequired: 'Please select parking type',
      areaRequired: 'Parking zone is required',
      rentableAreaRequired: 'Leasable area is required',
      statusAvailable: 'Available',
      statusRented: 'Rented',
      statusMaintenance: 'Maintenance',
    },

    // ==================== Office Space ====================
    officeSpace: {
      editTitle: 'Edit Office Space',
      addTitle: 'Add Office Space',
      code: 'Office Code',
      name: 'Office Name',
      type: 'Office Type',
      area: 'Floor Area',
      rentableArea: 'Leasable Area',
      standardRent: 'Standard Rent',
      minRent: 'Min Rent',
      managementFee: 'Mgmt Fee',
      managementDept: 'Management Dept',
      managementDeptPlaceholder: 'Select management department',
      codePlaceholder: 'Enter office code',
      namePlaceholder: 'Enter office name',
      typePlaceholder: 'Select office type',
      areaPlaceholder: 'Enter floor area',
      rentableAreaPlaceholder: 'Enter leasable area',
      rentPlaceholder: 'Enter amount',
      feePlaceholder: 'Enter mgmt fee',
      codeRequired: 'Please enter office code',
      nameRequired: 'Please enter office name',
      typeRequired: 'Please select office type',
      areaRequired: 'Floor area is required',
      rentableAreaRequired: 'Leasable area is required',
      statusAvailable: 'Available',
      statusRented: 'Rented',
      statusMaintenance: 'Maintenance',
    },

    // ==================== Building ====================
    building: {
      title: 'Building',
      editTitle: 'Edit Building',
      addTitle: 'Add Building',
      name: 'Building Name',
      code: 'Building Code',
      type: 'Building Type',
      floorCount: 'Floor Count',
      totalArea: 'Floor Area',
      constructionYear: 'Construction Year',
      structureType: 'Structure Type',
      namePlaceholder: 'Enter building name',
      codePlaceholder: 'Enter building code',
      typePlaceholder: 'Select building type',
      yearPlaceholder: 'Select year',
      structurePlaceholder: 'Enter structure type',
      floorCountPlaceholder: 'Enter floor count',
      totalAreaPlaceholder: 'Enter floor area',
      sortOrderPlaceholder: 'Enter sort order',
      nameRequired: 'Building name is required',
      orgRequired: 'Please select the organization',
      codeRequired: 'Building code is required',
      typeRequired: 'Building type is required',
      floorCountRequired: 'Floor count is required',
      totalAreaRequired: 'Gross floor area is required',
      searchPlaceholder: 'Search buildings...',
      noBuildings: 'No buildings',
      floorUnit: '{count} floors',
      typeMall: 'Mall',
      typeOffice: 'Office',
      typeResidential: 'Residential',
      typeHotel: 'Hotel',
      typeOther: 'Other',
      codeAutoGenerated: '(Auto)',
    },

    // ==================== Floor ====================
    floor: {
      title: 'Floor',
      noFloors: 'No floors',
      addFloor: 'Add Floor',
      editTitle: 'Edit Floor',
      addTitle: 'Add Floor',
      name: 'Floor Name',
      code: 'Floor Code',
      floorNumber: 'Floor No.',
      totalArea: 'Floor Area',
      rentableArea: 'Leasable Area',
      mainBusinessType: 'Main Business Type',
      namePlaceholder: 'Enter floor name',
      codePlaceholder: 'Enter floor code',
      businessTypePlaceholder: 'Select main business type',
      nameRequired: 'Floor name is required',
      codeRequired: 'Floor code is required',
      floorNumberRequired: 'Floor number is required',
      totalAreaRequired: 'Gross floor area is required',
      rentableAreaRequired: 'Gross leasable area is required',
      statusEnabled: 'Enabled',
      statusDisabled: 'Disabled',
      rentableSuffix: 'rentable',
      loadFailed: 'Failed to load floors',
      codeAutoGenerated: '(Auto)',
      businessRetail: 'Retail',
      businessFood: 'Food & Beverage',
      businessEntertainment: 'Entertainment',
      businessService: 'Service',
      businessMixed: 'Mixed',
      // Batch creation
      floorRange: 'Floor Range',
      from: 'From',
      to: 'To',
      willCreate: 'Will create',
      layers: 'floors',
      layer: 'floor',
      namePrefix: 'Name Prefix',
      namePrefixPlaceholder: 'e.g., Anchor Store (floor number will be appended)',
      namePrefixHint: 'Leave empty to use default naming: floor number + layer',
      codeAutoGeneratedTip: 'Auto-generated by system',
      skipAndContinue: 'Skip & Continue',
      batchCreateSuccess: 'Successfully created {count} floors',
      batchCreatePartialSuccess: 'Success {success}, Failed {fail}',
      allFloorsExist: 'All selected floor numbers already exist, no creation needed',
      existingFloorsConfirm: 'Floor numbers {floors} already exist. Skip these and continue creating the rest?',
    },

    // ==================== Shop ====================
    shop: {
      title: 'Shop',
      editTitle: 'Edit Shop',
      addTitle: 'Add Shop',
      name: 'Asset Name',
      code: 'Asset Code',
      type: 'Shop Type',
      area: 'Floor Area',
      rentableArea: 'Leasable Area',
      minRent: 'Min Rent',
      standardRent: 'Standard Rent',
      managementFee: 'Management Fee',
      position: 'Position',
      plannedBusiness: 'Planned Business',
      namePlaceholder: 'Enter asset name',
      codePlaceholder: 'Enter asset code',
      typePlaceholder: 'Select shop type',
      positionPlaceholder: 'Enter position description or coordinate JSON',
      plannedBusinessPlaceholder: 'Enter planned business',
      managementDept: 'Management Dept',
      managementDeptPlaceholder: 'Select management department',
      managementDeptRequired: 'Management dept is required',
      sortOrderPlaceholder: 'Enter sort order',
      nameRequired: 'Asset name is required',
      codeRequired: 'Asset code is required',
      typeRequired: 'Shop type is required',
      areaRequired: 'Floor area is required',
      rentableAreaRequired: 'Leasable area is required',
      rentableAreaPlaceholder: 'Enter leasable area',
      addShop: 'Add Shop',
      visualView: 'Visual View',
      listView: 'List View',
      statTotal: 'Total',
      statAvailable: 'Available',
      statRented: 'Rented',
      statMaintenance: 'Maintenance',
      statReserved: 'Reserved',
      noShops: 'No asset data',
      typeRetail: 'Retail',
      typeFood: 'Food & Beverage',
      typeEntertainment: 'Entertainment',
      typeService: 'Service',
      statusAvailable: 'Available',
      statusRented: 'Rented',
      statusMaintenance: 'Maintenance',
      statusReserved: 'Reserved',
    },

    // ==================== Asset Table ====================
    table: {
      searchPlaceholder: 'Search code or name',
      typeFilter: 'Asset Type',
      statusFilter: 'Status',
      columnType: 'Type',
      columnCode: 'Code',
      columnName: 'Name',
      columnManagementDept: 'Management Dept',
      columnArea: 'GFA',
      columnRentableArea: 'GLA',
      columnStatus: 'Status',
      columnOperation: 'Operation',
      noData: 'No asset data',
    },

    // ==================== Manage Panel ====================
    manage: {
      title: 'Asset Management',
      subtitle: 'Building - Floor - Multi-type Asset Management',
      visualView: 'Visual',
      listView: 'List',
      totalAssets: 'Total Assets',
      addAsset: 'Add Asset',
      selectFloorFirst: 'Please select a floor first',
      selectBuildingFirst: 'Please select a building first',
      selectBuildingPlaceholder: 'Select building',
      loadFailed: 'Failed to load asset data',
      deleteFailed: 'Delete failed',
      deleteAssetConfirm: 'Confirm delete {type} "{name}"?',
      deleteBuildingConfirm: 'Confirm delete building "{name}"? All associated floors and shops will also be deleted.',
      deleteFloorConfirm: 'Confirm delete floor "{name}"? All associated shops will also be deleted.',
      svgNoShapes: 'No valid shapes detected in SVG',
      pngNoRegions: 'No colored shop regions detected; imported as plain background',
      pngParseFail: 'Image recognition failed',
      svgSlimmed: 'Large SVG auto-optimized: {removed} decorative lines stripped ({fromMB}MB → {toMB}MB). Export PNG for full texture',
      svgClearedCount: 'Cleared {count} existing assets from floor',
      svgImportSuccess: 'Import successful: created {success} assets',
      bgImportSuccess: 'Background imported successfully',
      svgImportFailed: ', {fail} failed',
      svgImporting: 'Importing assets, please wait...',
      svgBatchFailed: 'Batch creation failed',
      svgImportError: 'Import failed: {msg}',
      svgImportTimeout: 'Asset creation request sent, may still be processing. Please refresh to check results',
      refreshFailed: 'Failed to refresh asset data',
      assetTypeShop: 'Shop',
      assetTypeAdvertising: 'Advertising',
      assetTypeSharedSpace: 'Shared Space',
      assetTypeWarehouse: 'Warehouse',
      assetTypeParking: 'Parking',
      assetTypeOfficeSpace: 'Office Space',
    },

    // ==================== Visual Panel ====================
    visualPanel: {
      title: 'Asset Visualization',
      floorSelected: 'Floor selected',
    },

    // ==================== Archive / Advertising List ====================
    archive: {
      title: 'Asset Archive',
      developing: 'Asset archive is under development...',
    },
    advertisingList: {
      title: 'Advertising Management',
      developing: 'Advertising management is under development...',
    },

    // ==================== Building List ====================
    buildingList: {
      keyword: 'Keyword',
      keywordPlaceholder: 'Enter building name/code/address',
      addBuilding: 'Add Building',
      editBuilding: 'Edit Building',
      buildingDetail: 'Building Details',
      deleteConfirm: 'Confirm delete building with ID "{ids}"?',
      enableSuccess: 'Enabled successfully',
      disableSuccess: 'Disabled successfully',
      addSuccess: 'Added successfully',
      editSuccess: 'Updated successfully',
      enable: 'Enable',
      disable: 'Disable',
      sortOrder: 'Sort Order',
      remarkLabel: 'Remark',
      remarkPlaceholder: 'Enter content',
      address: 'Address',
      statusLabel: 'Status',
      createTime: 'Create Time',
      updateTime: 'Update Time',
    },

    // ==================== Common Form ====================
    form: {
      statusRequired: 'Please select status',
      statusPlaceholder: 'Select status',
      remarkPlaceholder: 'Enter remark',
      addressPlaceholder: 'Enter address',
      codeExists: 'This code already exists, please use a different one',
      loadFailed: 'Failed to load data',
      updateSuccess: 'Updated successfully',
      createSuccess: 'Created successfully',
    },

    // ==================== Split & Merge ====================
    splitMerge: {
      splitOrder: 'Split Order',
      mergeOrder: 'Merge Order',
      splitOrders: 'Split Orders',
      mergeOrders: 'Merge Orders',
      createSplit: 'Create Split Order',
      createMerge: 'Create Merge Order',
      orderNo: 'Order No.',
      sourceAsset: 'Source Asset',
      targetAsset: 'Target Asset',
      effectiveDate: 'Effective Date',
      splitCount: 'Split Count',
      sourceCount: 'Merge Count',
      splitMethod: 'Split Method',
      reason: 'Reason',
      isAdjacent: 'Adjacent',
      adjacent: 'Adjacent',
      notAdjacent: 'Not Adjacent',
      newCode: 'New Code',
      newName: 'New Name',
      newArea: 'New Area',
      targetCode: 'Target Code',
      targetName: 'Target Name',
      targetArea: 'Target Area',
      status: {
        DRAFT: 'Draft',
        PENDING: 'Pending',
        APPROVED: 'Approved',
        EFFECTIVE: 'Effective',
        CANCELLED: 'Cancelled',
      },
      actions: {
        submit: 'Submit',
        approve: 'Approve',
        reject: 'Reject',
        cancel: 'Cancel',
        split: 'Split',
        merge: 'Merge',
        lineage: 'Lineage',
      },
      contextMenu: {
        split: 'Split this asset',
        merge: 'Merge selected assets',
        lineage: 'View lineage',
        reOutline: 'Re-outline Position',
      },
      lineage: {
        title: 'Asset Lineage',
        parent: 'Source',
        child: 'Destination',
        noData: 'No lineage records',
      },
      visual: 'Visual',
      form: 'Form',
      areaExceeded: 'Area exceeds source asset',
      areaMismatch: 'Total split area does not match source asset area',
      splitCreatedAndSubmitted: 'Split order created and submitted for approval',
      splitExecuted: 'Split executed, source archived, new assets created',
    },

    // ==================== Asset Adjustment ====================
    adjustment: {
      title: 'Asset Adjustment',
      adjustType: 'Adjustment Type',
      split: 'Split',
      merge: 'Merge',
      all: 'All',
      orderNo: 'Order No.',
      sourceInfo: 'Source Asset',
      effectiveDate: 'Effective Date',
      reason: 'Reason',
      status: 'Status',
      createTime: 'Create Time',
      createBy: 'Created By',
      operation: 'Operation',
      addSplit: 'New Split',
      addMerge: 'New Merge',
      batchDelete: 'Batch Delete',
      backToList: 'Back to List',
      save: 'Save',
      submit: 'Submit',
      approve: 'Approve',
      reject: 'Reject',
      cancel: 'Cancel',
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      newRecord: 'New',
      sourceAssetType: 'Source Asset Type',
      sourceAssetId: 'Source Asset ID',
      buildingId: 'Building',
      floorId: 'Floor',
      splitMethod: 'Split Method',
      visual: 'Visual',
      form: 'Form',
      splitItems: 'Split Items',
      addItem: 'Add Row',
      removeItem: 'Remove',
      newCode: 'New Code',
      newName: 'New Name',
      newArea: 'New Area',
      areaSummary: 'Area Total',
      areaUnit: 'sqm',
      areaExceeded: 'Area exceeds source asset',
      sourceAssets: 'Source Assets',
      addAsset: 'Add Asset',
      isAdjacent: 'Adjacent',
      adjacent: 'Adjacent',
      notAdjacent: 'Not Adjacent',
      targetCode: 'Target Code',
      targetName: 'Target Name',
      targetArea: 'Target Area',
      targetAsset: 'Target Asset',
      otherInfo: 'Other Info',
      filterBuilding: 'Building',
      filterFloor: 'Floor',
      filterDateRange: 'Date Range',
      search: 'Search',
      reset: 'Reset',
      noData: 'No Data',
      confirmDelete: 'Confirm delete this record?',
      confirmCancel: 'Confirm cancel this record?',
      rejectReason: 'Reject Reason',
      approveSuccess: 'Approved successfully',
      rejectSuccess: 'Rejected successfully',
      cancelSuccess: 'Cancelled successfully',
      deleteSuccess: 'Deleted successfully',
      saveSuccess: 'Saved successfully',
      submitSuccess: 'Submitted for approval',
      assetHasPendingOrders: 'This asset already has pending split/merge orders',
      statusDraft: 'Draft',
      statusPending: 'Pending',
      statusApproved: 'Approved',
      statusEffective: 'Effective',
      statusCancelled: 'Cancelled',
    },
  },
}
